import { Link } from "react-router-dom";
import { Coins } from "lucide-react";
import { useCredits } from "@/hooks/useCredits";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

interface CreditBalanceChipProps {
  className?: string;
  /** When true, render a static (non-link) version — e.g. inside dropdowns. */
  staticDisplay?: boolean;
}

/** Small pill that shows the signed-in user's credit balance. */
export function CreditBalanceChip({ className, staticDisplay }: CreditBalanceChipProps) {
  const { user } = useAuth();
  const { balance, loading } = useCredits();
  const { t } = useLanguage();

  if (!user) return null;

  const display = loading && balance === null ? "…" : String(balance ?? 0);
  const label = t("credits.balance.chip", { n: display });

  const inner = (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold whitespace-nowrap",
        !staticDisplay && "hover:bg-primary/20 transition-colors cursor-pointer",
        className,
      )}
      aria-label={label}
    >
      <Coins className="h-3.5 w-3.5" aria-hidden />
      {label}
    </span>
  );

  if (staticDisplay) return inner;
  return <Link to="/credits">{inner}</Link>;
}
